import { createRef, Component } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Grid from 'beam/dist/Grid'
import withStyles from 'beam/dist/styles'
import Typography from 'beam/dist/Typography'
import Icon from 'beam/dist/Icon'
import { msToHHMMSS } from 'beam/dist/utils/time'
import TranscriptionText from './TranscriptionText'
import Sentiment from '../const/sentiment'
import { withTranslation } from 'react-i18next'
import { SCROLL_ALIGNMENT_FRACTION } from '../MediaPlayer/helpers'
import { withFeatureFlags, FEATURE_FLAGS } from '../../../FeatureFlags'

const styles = theme => ({
  bubble: {
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.5),
    padding: theme.spacing(1, 1.5),
    maxWidth: `calc(100% - ${theme.spacing(12)}px)`,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    transform: 'scale(0.975)',
  },
  bubbleAgent: {
    borderRadius: `${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius * 4}px 0 ${theme.shape.borderRadius *
      4}px`,
    background: theme.palette.color.lightBlue[800],
    alignSelf: 'flex-end',
  },
  bubbleCustomer: {
    borderRadius: `${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius *
      4}px 0`,
    background: theme.palette.grey['300'],
    alignSelf: 'flex-start',
  },
  isActive: {
    boxShadow: `0 0 0 3px ${theme.palette.color.red[700]}`,
    transform: 'scale(1)',
  },
  timestamp: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
  },
  lightLabel: {
    color: theme.palette.primary.contrastText,
    opacity: 0.54,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sentimentLabel: {
    color: theme.palette.text.secondary,
  },
  darkIcon: {},
})

class SpeechBubble extends Component {
  static propTypes = {
    start: PropTypes.number.isRequired,
    end: PropTypes.number.isRequired,
    sentiment: PropTypes.number,
    channel: PropTypes.number.isRequired,
    clumps: PropTypes.array.isRequired,
    queryCurrentIndex: PropTypes.number.isRequired,
    classes: PropTypes.object.isRequired,
    setTime: PropTypes.func.isRequired,
    currentTime: PropTypes.number.isRequired,
    scrollElement: PropTypes.element,
    t: PropTypes.func,
    canUseFeature: PropTypes.func,
  }

  static defaultProps = {
    sentiment: 0,
    scrollElement: null,
  }

  static getSentimentIcon = sentiment => {
    switch (Sentiment.getStateFromScore(sentiment)) {
      case Sentiment.state.POSITIVE:
        return 'EmoticonHappyOutline'
      case Sentiment.state.NEGATIVE:
        return 'EmoticonSadOutline'
      default:
        return false
    }
  }

  state = {
    hasBeenRendered: false,
  }

  constructor(props) {
    super(props)
    this.bubbleRef = createRef()
    this.isAdvancedSentimentEnabled = this.props.canUseFeature(FEATURE_FLAGS.ANALYTICS_ENABLE_ADVANCED_SENTIMENT)
  }

  componentDidUpdate(prevProps) {
    const { currentTime, start, end, scrollElement } = this.props
    const { hasBeenRendered } = this.state
    const isInRange = this.inRange(currentTime, start, end)
    const wasInRange = this.inRange(prevProps.currentTime, prevProps.start, prevProps.end)

    // Scroll to the current query result
    for (const clump of this.props.clumps) {
      if (clump.qIndex === this.props.queryCurrentIndex) {
        this.scrollIntoView(scrollElement)
        break
      }
    }

    // Only scroll to selected bubble if we're not performing a search
    if (this.props.queryCurrentIndex <= 0) {
      this.scrollIntoView(
        scrollElement &&
          this.bubbleRef &&
          this.bubbleRef.current &&
          isInRange &&
          //On initial render, we don't care whether or not it was in range before
          (isInRange !== wasInRange || !hasBeenRendered),
      )
    }

    if (!hasBeenRendered) {
      this.setState({ hasBeenRendered: true })
    }
  }

  scrollIntoView(willScroll) {
    const { scrollElement } = this.props

    if (willScroll) {
      const bubble = this.bubbleRef.current

      const bubblePosition = bubble.offsetTop
      const bubbleHeight = bubble.offsetHeight
      const scrollElementHeight = scrollElement.offsetHeight

      const scrollPosition =
        bubbleHeight < scrollElementHeight
          ? //Position + bubble center + scrollElement center = center of screen
          bubblePosition + bubbleHeight / SCROLL_ALIGNMENT_FRACTION - scrollElementHeight / SCROLL_ALIGNMENT_FRACTION
          : //If bubble is big, just go to the top
          bubblePosition

      if (scrollElement.scrollTo) {
        scrollElement.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        })
      } else {
        scrollElement.scrollTop = scrollPosition
      }
    }
  }

  inRange(val, start, end) {
    const roundedVal = Math.round(val / 1000)
    const roundedStart = Math.round(start / 1000)
    const roundedEnd = Math.round(end / 1000)
    return roundedVal >= roundedStart && roundedVal <= roundedEnd
  }

  render() {
    const { start, end, channel, clumps, queryCurrentIndex, classes, setTime, currentTime, sentiment, t } = this.props

    const isAgent = channel === 1
    const isCurrentBubble = this.inRange(currentTime, start, end)
    const sentimentIcon = SpeechBubble.getSentimentIcon(sentiment)
    const variant = isAgent ? 'light' : 'dark'

    return (
      <div
        ref={this.bubbleRef}
        className={classNames(classes.bubble, isAgent ? classes.bubbleAgent : classes.bubbleCustomer, {
          [classes.isActive]: isCurrentBubble,
        })}
        role="button"
        tabIndex={0}
        onClick={() => setTime(start)}
        onKeyPress={() => setTime(start)}
      >
        <TranscriptionText
          queryCurrentIndex={queryCurrentIndex}
          setTime={setTime}
          clumps={clumps}
          currentTime={currentTime}
          variant={variant}
        />
        <div className={classes.footer}>
          {sentimentIcon && !this.isAdvancedSentimentEnabled && (
            <Grid container spacing={1} alignItems="center" alignContent="center">
              <Grid item>
                <Typography
                  variant="caption"
                  gutterBottom
                  inline
                  align="center"
                  className={isAgent ? classes.lightLabel : classes.sentimentLabel}
                >
                  {t('sentiment')}
                </Typography>
              </Grid>
              <Grid item>
                <Icon
                  className={classNames([
                    classes[`${variant}Icon`],
                    isAgent ? classes.lightLabel : classes.sentimentLabel,
                  ])}
                >
                  {sentimentIcon}
                </Icon>
              </Grid>
            </Grid>
          )}
          <Typography
            className={classNames([classes.timestamp, isAgent && classes.lightLabel])}
            align="right"
            variant="caption"
          >
            {msToHHMMSS(start)}
          </Typography>
        </div>
      </div>
    )
  }
}

export { SpeechBubble as UnstyledSpeechBubble }
export default withFeatureFlags(withTranslation()(withStyles(styles)(SpeechBubble)))
